import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/pages/CollegePredictorPage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  GraduationCap, CalendarClock, Sparkles, BadgeCheck, Loader2, Send, ShieldCheck, CheckCircle2, ArrowRight,
} from "lucide-react";

export default function RecommendedCollegesPage() {
  const [colleges, setColleges] = useState([]);
  const [apps, setApps] = useState([]);

  const load = () => {
    api.get("/colleges/recommended").then((r) => setColleges(r.data)).catch(() => {});
    api.get("/colleges/recommended/applications/me").then((r) => setApps(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
      <Header icon={GraduationCap} eyebrow="Apply Now"
        title="Recommended colleges to apply" desc="Hand-picked & AI-matched to your profile, interests and predicted eligibility." />

      {apps.length > 0 && (
        <div className="mb-8 rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.04] p-5">
          <h3 className="font-display text-base font-semibold mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" /> Your applications ({apps.length})
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {apps.map((a) => (
              <div key={a.id} data-testid="my-application-row" className="rounded-xl bg-white/[0.03] p-3">
                <p className="text-sm font-medium text-white">{a.college_name}</p>
                <p className="text-xs text-slate-400">{a.program}</p>
                <span className="mt-1 inline-block rounded-full bg-emerald-400/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">{a.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {colleges.map((c, i) => (
          <CollegeCard key={c.id} college={c} index={i} onApplied={load} />
        ))}
      </div>
    </div>
  );
}

function CollegeCard({ college: c, index, onApplied }) {
  useEffect(() => { api.post(`/colleges/recommended/${c.id}/view`).catch(() => {}); }, [c.id]);

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
      data-testid="recommended-college-card"
      className="flex flex-col overflow-hidden rounded-2xl border border-white/8 bg-[#131C2E]">
      <div className="relative h-36 overflow-hidden">
        <img src={c.image_url || "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=800&q=70"} alt={c.name}
          onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=800&q=70"; }}
          className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#131C2E] to-transparent" />
        {c.match_score > 0 && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-cyan-400/90 px-2.5 py-1 text-[10px] font-bold text-[#041016]">
            <Sparkles className="h-3 w-3" /> {c.match_score}% AI match
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-display font-semibold text-white">{c.name}</h3>
        <p className="text-sm text-cyan-300">{c.program}</p>
        <p className="mt-2 text-xs text-slate-400 line-clamp-3 flex-1">{c.description}</p>
        <div className="mt-3 flex items-center gap-1 text-xs text-amber-300">
          <CalendarClock className="h-3.5 w-3.5" /> Deadline: {c.deadline}
        </div>
        <div className="mt-4">
          {c.applied ? (
            <span data-testid="already-applied-badge" className="flex items-center justify-center gap-2 rounded-full bg-emerald-400/15 px-4 py-2.5 text-sm font-bold text-emerald-300">
              <BadgeCheck className="h-4 w-4" /> Applied
            </span>
          ) : (
            <ApplyDialog college={c} onApplied={onApplied} />
          )}
        </div>
      </div>
    </motion.div>
  );
}

function ApplyDialog({ college, onApplied }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    student_name: user?.name || "", mobile: "", email: user?.email || "",
    class_qualification: "", program: college.program, city_state: "", whatsapp_optin: true,
  });
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const sendOtp = async () => {
    if (!form.student_name || !form.mobile || !form.email || !form.class_qualification || !form.city_state) {
      toast.info("Please fill all fields"); return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/otp/send", { mobile: form.mobile });
      setDemoOtp(data.demo_otp || "");
      setStep(2);
      toast.success("OTP sent to your mobile");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send OTP");
    } finally { setLoading(false); }
  };

  const submit = async () => {
    if (!otp || otp.length < 6) { toast.info("Enter the 6-digit OTP"); return; }
    setLoading(true);
    try {
      await api.post(`/colleges/recommended/${college.id}/apply`, { ...form, otp });
      setDone(true);
      toast.success("Application submitted!");
      onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setLoading(false); }
  };

  const reset = () => { setStep(1); setOtp(""); setDemoOtp(""); setDone(false); };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <button data-testid="apply-now-btn"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-cyan-400 px-4 py-2.5 text-sm font-bold text-[#041016] transition-transform hover:scale-[1.02]">
          Apply Now <ArrowRight className="h-4 w-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#131C2E] border-white/10 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Apply · {college.name}</DialogTitle></DialogHeader>

        {done ? (
          <div className="text-center py-6">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-400/15 glow-emerald">
              <CheckCircle2 className="h-7 w-7 text-emerald-400" />
            </div>
            <p className="font-semibold">Application submitted!</p>
            <p className="text-sm text-slate-400 mt-1">You'll get updates & reminders about {college.name}.</p>
            <a href={college.application_link} target="_blank" rel="noreferrer"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2 text-sm font-bold text-[#041016]">
              Continue on college site <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        ) : step === 1 ? (
          <div className="space-y-3 pt-2">
            <F label="Full Name" testid="apply-name" value={form.student_name} onChange={(v) => setForm({ ...form, student_name: v })} />
            <F label="Mobile Number" testid="apply-mobile" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} placeholder="10-digit mobile" />
            <F label="Email" testid="apply-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <F label="Class / Qualification" testid="apply-class" value={form.class_qualification} onChange={(v) => setForm({ ...form, class_qualification: v })} placeholder="e.g. Class 12 / Graduate" />
            <F label="Course / Program" testid="apply-program" value={form.program} onChange={(v) => setForm({ ...form, program: v })} />
            <F label="City / State" testid="apply-city" value={form.city_state} onChange={(v) => setForm({ ...form, city_state: v })} />
            <label data-testid="whatsapp-optin-label" className="flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] p-3 cursor-pointer">
              <input type="checkbox" data-testid="whatsapp-optin-checkbox" checked={form.whatsapp_optin}
                onChange={(e) => setForm({ ...form, whatsapp_optin: e.target.checked })}
                className="mt-0.5 h-4 w-4 accent-cyan-400" />
              <span className="text-xs text-slate-300">Send me <span className="font-semibold text-white">WhatsApp updates</span> about my application status. I consent to receiving messages on this number.</span>
            </label>
            <button data-testid="send-otp-btn" onClick={sendOtp} disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-400 px-6 py-3 text-sm font-bold text-[#041016] disabled:opacity-50">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4" /> Verify Mobile & Continue</>}
            </button>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-3 text-xs text-cyan-300">
              We sent a 6-digit OTP to {form.mobile}. {demoOtp && <span className="font-bold">Demo OTP: {demoOtp}</span>}
            </div>
            <div>
              <label className="text-sm text-slate-300">Enter OTP</label>
              <input data-testid="otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="••••••" maxLength={6}
                className="mt-1 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-center font-mono-data text-2xl tracking-[0.5em] text-white outline-none focus:border-cyan-400/50" />
            </div>
            <button data-testid="submit-application-btn" onClick={submit} disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-400 px-6 py-3 text-sm font-bold text-[#041016] disabled:opacity-50">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Submit Application</>}
            </button>
            <button onClick={() => setStep(1)} className="w-full text-xs text-slate-400 hover:text-white">← Edit details</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, testid, value, onChange, placeholder }) => (
  <div>
    <label className="text-sm text-slate-300">{label}</label>
    <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="mt-1 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50" />
  </div>
);
